/**
 * Assessment questions data
 */
import { Question, Category } from '../models';

const questions: Question[] = [
  {
    id: 'q1',
    text: 'How well are PMO initiatives aligned with your organization\'s strategic objectives?',
    category: 'strategy-alignment',
    order: 1,
    options: [
      { id: 'q1-a', text: 'Alignment is minimal or informal; projects are chosen ad-hoc.', score: 1 },
      { id: 'q1-b', text: 'Some projects align with strategy, but prioritization criteria are informal.', score: 2 },
      { id: 'q1-c', text: 'Formal processes ensure most projects are evaluated and prioritized for strategic fit.', score: 3 },
      { id: 'q1-d', text: 'Portfolio planning is fully integrated with strategic planning; executives drive alignment.', score: 4 }
    ]
  },
  {
    id: 'q2',
    text: 'To what extent does the PMO participate in setting strategic priorities?',
    category: 'strategy-alignment',
    order: 2,
    options: [
      { id: 'q2-a', text: 'PMO is not involved; priorities are set without PMO input.', score: 1 },
      { id: 'q2-b', text: 'PMO is occasionally consulted, but has limited influence on strategy.', score: 2 },
      { id: 'q2-c', text: 'PMO regularly contributes to strategy discussions and helps shape priorities.', score: 3 },
      { id: 'q2-d', text: 'PMO co-leads the strategic planning process and ensures execution aligns with top priorities.', score: 4 }
    ]
  },
  {
    id: 'q3',
    text: 'What is the level of executive leadership support and sponsorship for the PMO and its portfolio?',
    category: 'strategy-alignment',
    order: 3,
    options: [
      { id: 'q3-a', text: 'No dedicated executive sponsor; PMO operates without visible leadership support.', score: 1 },
      { id: 'q3-b', text: 'Some executive oversight exists, but involvement is sporadic or limited.', score: 2 },
      { id: 'q3-c', text: 'A designated executive sponsor regularly reviews PMO plans and provides guidance.', score: 3 },
      { id: 'q3-d', text: 'Senior executives actively champion the PMO; they engage in governance and resource decisions.', score: 4 }
    ]
  },
  {
    id: 'q4',
    text: 'How would you characterize the skills and development of the PMO staff?',
    category: 'people',
    order: 4,
    options: [
      { id: 'q4-a', text: 'Roles are assigned without formal qualifications or consistent training.', score: 1 },
      { id: 'q4-b', text: 'Some staff are trained or certified, but skills vary and gaps remain.', score: 2 },
      { id: 'q4-c', text: 'Team members meet defined competency profiles, and training plans address most gaps.', score: 3 },
      { id: 'q4-d', text: 'The PMO team is highly skilled; ongoing professional development and certifications are standard.', score: 4 }
    ]
  },
  {
    id: 'q5',
    text: 'How effectively does your PMO manage resource capacity and staffing?',
    category: 'people',
    order: 5,
    options: [
      { id: 'q5-a', text: 'Resource planning is ad-hoc; staffing shortages are common and unplanned.', score: 1 },
      { id: 'q5-b', text: 'Basic resource plans exist, but resources are often over-allocated and cause delays.', score: 2 },
      { id: 'q5-c', text: 'Resources are planned in advance; cross-training helps cover project needs.', score: 3 },
      { id: 'q5-d', text: 'Proactive capacity planning and flexible resourcing ensure all project needs are met.', score: 4 }
    ]
  },
  // Adding 5 more questions to keep the file size manageable
  {
    id: 'q6',
    text: 'How mature are your PMO\'s portfolio reporting and dashboard capabilities?',
    category: 'portfolio-reporting',
    order: 6,
    options: [
      { id: 'q6-a', text: 'No standardized reporting; data is collected manually and reports are infrequent.', score: 1 },
      { id: 'q6-b', text: 'Basic status reports exist for some projects, but are mostly static and delivered irregularly.', score: 2 },
      { id: 'q6-c', text: 'Standard dashboards provide regular updates on project status and key performance indicators.', score: 3 },
      { id: 'q6-d', text: 'Advanced dashboards and analytics are automated, providing real-time insights into portfolio performance.', score: 4 }
    ]
  },
  {
    id: 'q7',
    text: 'What level of transparency do stakeholders have into project and portfolio information?',
    category: 'portfolio-reporting',
    order: 7,
    options: [
      { id: 'q7-a', text: 'Stakeholders have very limited visibility; reports are rarely shared.', score: 1 },
      { id: 'q7-b', text: 'Occasional reports are shared with executives, but many stakeholders rely on ad-hoc updates.', score: 2 },
      { id: 'q7-c', text: 'Regular, detailed reports are published for all stakeholders, with opportunities for their feedback.', score: 3 },
      { id: 'q7-d', text: 'Stakeholders have self-service access to live dashboards and actively engage with performance data.', score: 4 }
    ]
  },
  {
    id: 'q8',
    text: 'How does the PMO handle an increasing number or size of projects?',
    category: 'scalability-growth',
    order: 8,
    options: [
      { id: 'q8-a', text: 'Growth overwhelms the PMO; new projects are added without scalable processes.', score: 1 },
      { id: 'q8-b', text: 'There is some effort to manage growth, but processes strain under higher volume.', score: 2 },
      { id: 'q8-c', text: 'The PMO has adaptable processes and staffing to manage moderate growth effectively.', score: 3 },
      { id: 'q8-d', text: 'Processes and resourcing are highly scalable; the PMO handles rapid expansion seamlessly.', score: 4 }
    ]
  },
  {
    id: 'q9',
    text: 'To what extent are your PMO processes standardized to enable scalability?',
    category: 'scalability-growth',
    order: 9,
    options: [
      { id: 'q9-a', text: 'No standardized processes; each project or department operates differently.', score: 1 },
      { id: 'q9-b', text: 'A few templates exist, but adoption is inconsistent across teams.', score: 2 },
      { id: 'q9-c', text: 'Consistent methodologies and templates are used across most projects.', score: 3 },
      { id: 'q9-d', text: 'Enterprise-wide standards are in place and continuously refined to support growth.', score: 4 }
    ]
  },
  {
    id: 'q10',
    text: 'What is the level of adoption of project management tools within your organization?',
    category: 'tech-tools',
    order: 10,
    options: [
      { id: 'q10-a', text: 'Projects are managed using general tools (spreadsheets, email); no dedicated PMO system.', score: 1 },
      { id: 'q10-b', text: 'Different teams use various PM tools; there is no single platform for all projects.', score: 2 },
      { id: 'q10-c', text: 'A standard PM/PPM tool is adopted across the organization with basic functionality.', score: 3 },
      { id: 'q10-d', text: 'An integrated suite of advanced PM/PPM tools and automation supports all projects.', score: 4 }
    ]
  },
  {
    id: 'q11',
    text: 'How automated and integrated are your project management processes?',
    category: 'tech-tools',
    order: 11,
    options: [
      { id: 'q11-a', text: 'Processes are manual; data is not integrated between systems.', score: 1 },
      { id: 'q11-b', text: 'Partial automation exists (e.g., scheduling), but many tasks require manual work and data reconciliation.', score: 2 },
      { id: 'q11-c', text: 'Key workflows are automated and major systems (e.g., ERP, PM) are integrated for efficiency.', score: 3 },
      { id: 'q11-d', text: 'End-to-end automation with AI/ML insights; systems seamlessly share data and trigger actions.', score: 4 }
    ]
  },
  {
    id: 'q12',
    text: 'How effectively is data used to drive decision-making in the PMO?',
    category: 'tech-tools',
    order: 12,
    options: [
      { id: 'q12-a', text: 'Decisions are based on anecdotal evidence; no systematic data analysis.', score: 1 },
      { id: 'q12-b', text: 'Basic metrics are tracked (e.g., milestones), but analysis is limited and retrospective.', score: 2 },
      { id: 'q12-c', text: 'Analytical tools provide forecasts and trends; decisions are data-informed.', score: 3 },
      { id: 'q12-d', text: 'Advanced analytics (predictive and prescriptive) are used for proactive decision-making and optimization.', score: 4 }
    ]
  },
  {
    id: 'q13',
    text: 'How does the PMO ensure projects deliver business value and benefits?',
    category: 'value-focus',
    order: 13,
    options: [
      { id: 'q13-a', text: 'Benefits planning is not formalized; success is measured only by project completion.', score: 1 },
      { id: 'q13-b', text: 'Benefits are identified at project start, but tracking is inconsistent or mostly after-the-fact.', score: 2 },
      { id: 'q13-c', text: 'A benefits realization framework tracks expected vs. actual outcomes for most projects.', score: 3 },
      { id: 'q13-d', text: 'Benefits management is integrated into governance; ROI and value metrics drive decisions continuously.', score: 4 }
    ]
  },
  {
    id: 'q14',
    text: 'How well does the PMO communicate and demonstrate project value to stakeholders?',
    category: 'value-focus',
    order: 14,
    options: [
      { id: 'q14-a', text: 'Value delivery is rarely reported; communications focus on tasks and outputs.', score: 1 },
      { id: 'q14-b', text: 'Occasional reports highlight benefits, but they lack systematic evidence.', score: 2 },
      { id: 'q14-c', text: 'Regular reporting of realized benefits and value metrics helps stakeholders understand impact.', score: 3 },
      { id: 'q14-d', text: 'The PMO proactively publishes success stories and quantitative value results; it is recognized as a value driver.', score: 4 }
    ]
  },
  {
    id: 'q15',
    text: 'What role does the PMO play in organizational change initiatives?',
    category: 'change-transformation',
    order: 15,
    options: [
      { id: 'q15-a', text: 'PMO is mostly reactive; it only manages projects without involvement in broader change efforts.', score: 1 },
      { id: 'q15-b', text: 'PMO occasionally supports change initiatives but lacks a formal change management approach.', score: 2 },
      { id: 'q15-c', text: 'Change management practices are integrated; PMO regularly supports and guides transformation projects.', score: 3 },
      { id: 'q15-d', text: 'PMO is a leader in change initiatives, embedding transformation and facilitating major organizational shifts.', score: 4 }
    ]
  },
  {
    id: 'q16',
    text: 'How does the PMO handle continuous improvement and lessons learned?',
    category: 'change-transformation',
    order: 16,
    options: [
      { id: 'q16-a', text: 'Lessons learned are rarely documented or applied; processes remain static.', score: 1 },
      { id: 'q16-b', text: 'Lessons learned are captured inconsistently; process improvements are occasional.', score: 2 },
      { id: 'q16-c', text: 'Formal lessons-learned reviews occur after each project; improvements are systematically applied.', score: 3 },
      { id: 'q16-d', text: 'A culture of continuous improvement exists; feedback loops and process refinements are ongoing.', score: 4 }
    ]
  },
  {
    id: 'q17',
    text: 'How resilient and adaptive is the PMO to unexpected disruptions?',
    category: 'change-transformation',
    order: 17,
    options: [
      { id: 'q17-a', text: 'The PMO struggles in crises; no contingency plans or resilience measures are in place.', score: 1 },
      { id: 'q17-b', text: 'Basic risk plans exist; the PMO can manage some disruptions but is often reactive.', score: 2 },
      { id: 'q17-c', text: 'The PMO has well-defined risk management and contingency processes; it handles most disruptions effectively.', score: 3 },
      { id: 'q17-d', text: 'The PMO proactively anticipates disruptions with flexible plans; it quickly adapts and continues delivering value.', score: 4 }
    ]
  },
  {
    id: 'q18',
    text: 'How standardized are your PMO processes and methodologies?',
    category: 'process-governance',
    order: 18,
    options: [
      { id: 'q18-a', text: 'No standardized processes; each project/team defines its own approach.', score: 1 },
      { id: 'q18-b', text: 'Key processes (e.g., initiation, closure) are defined, but not fully enforced.', score: 2 },
      { id: 'q18-c', text: 'A consistent methodology is used across the portfolio; adherence is monitored.', score: 3 },
      { id: 'q18-d', text: 'Processes are rigorously standardized and continuously optimized enterprise-wide.', score: 4 }
    ]
  },
  {
    id: 'q19',
    text: 'What governance structures (e.g., steering committees, review boards) does your PMO have?',
    category: 'process-governance',
    order: 19,
    options: [
      { id: 'q19-a', text: 'No formal governance; decisions are made on a project-by-project basis.', score: 1 },
      { id: 'q19-b', text: 'Some governance exists for major projects, but many lack oversight.', score: 2 },
      { id: 'q19-c', text: 'Formal governance bodies review all key projects; roles and approvals are well-defined.', score: 3 },
      { id: 'q19-d', text: 'A lean, effective governance framework is embedded organization-wide; it empowers timely decision-making.', score: 4 }
    ]
  },
  {
    id: 'q20',
    text: 'How are risk management and compliance integrated into your PMO?',
    category: 'process-governance',
    order: 20,
    options: [
      { id: 'q20-a', text: 'Risks are not formally tracked; compliance is managed only after issues occur.', score: 1 },
      { id: 'q20-b', text: 'Basic risk registers exist; compliance is checked at milestones, but processes are ad-hoc.', score: 2 },
      { id: 'q20-c', text: 'Risk management and compliance are integral parts of the project lifecycle (regular updates, audits).', score: 3 },
      { id: 'q20-d', text: 'Proactive risk mitigation and compliance are built into governance; the PMO regularly updates stakeholders on risk posture.', score: 4 }
    ]
  }
];

// The full assessment now includes all 20 questions as specified in the client requirements

export default questions;

// Helper function to get questions by category
export const getQuestionsByCategory = (category: Category): Question[] => {
  return questions.filter(q => q.category === category).sort((a, b) => a.order - b.order);
};

// Helper function to get all categories
export const getAllCategories = (): Category[] => {
  return Array.from(new Set(questions.map(q => q.category)));
};
